import { API_BASE_URL, authFetch, getHeaders as baseGetHeaders } from './authFetch';

const SCHEDULED_TASK_PREFIX = `${API_BASE_URL}/api/v1/scheduled_task`;
const FEED_REQUEST_TIMEOUT_MS = 30 * 60 * 1000;

function getHeaders(withJson = true): Record<string, string> {
  return baseGetHeaders(withJson);
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    let message = text;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (typeof parsed.detail === 'string') message = parsed.detail;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

async function feedAuthFetch(url: string, init: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FEED_REQUEST_TIMEOUT_MS);
  try {
    return await authFetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

export type FeedSourceType = 'news' | 'blog' | 'youtube' | 'discover';
export type FeedStatusState =
  | 'needs_interests'
  | 'generating'
  | 'ready'
  | 'fallback'
  | 'failed';

export interface FeedSourceStats {
  total_candidates?: number;
  selected_candidates?: number;
  by_source?: Partial<Record<FeedSourceType, number>>;
}

export interface FeedQualityScores {
  relevance?: number;
  freshness?: number;
  diversity?: number;
  delight?: number;
}

export interface FeedCardScore {
  relevance?: number;
  freshness?: number;
  trust?: number;
  delight?: number;
}

export interface FeedCard {
  card_id: string;
  title: string;
  summary: string;
  original_title?: string | null;
  original_summary?: string | null;
  story_id?: string | null;
  why_it_matters: string;
  source_type: FeedSourceType;
  source_name: string;
  source_url: string;
  published_at?: string | null;
  thumbnail_url?: string | null;
  interest_key: string;
  dedupe_key: string;
  scores?: FeedCardScore | null;
}

export interface FeedLeadCardSource {
  title: string;
  source_name: string;
  source_url: string;
  source_type: FeedSourceType;
}

export interface FeedLeadCard {
  card_id: string;
  title: string;
  snippet: string;
  detail_body: string;
  key_points: string[];
  source_links: FeedLeadCardSource[];
  interest_key: string;
  related_dedupe_keys: string[];
}

export interface FeedLane {
  lane_id: string;
  title: string;
  interest_key: string;
  description?: string | null;
  lead_card?: FeedLeadCard | null;
  items: FeedCard[];
}

export interface FeedDailySnapshot {
  snapshot_id: string;
  user_id: string;
  local_date: string;
  timezone: string;
  published_at: string;
  interests: string[];
  hero_cards: FeedCard[];
  lanes: FeedLane[];
  source_stats?: FeedSourceStats | null;
  quality_scores?: FeedQualityScores | null;
  generation_reason?: string | null;
  fallback_from_snapshot_id?: string | null;
}

export interface FeedStatusResponse {
  task_key: string;
  state: FeedStatusState;
  has_interests: boolean;
  interests: string[];
  next_run_at: string | null;
  last_success_at: string | null;
  latest_snapshot_local_date: string | null;
  needs_timezone_sync: boolean;
  is_generating: boolean;
  is_fallback: boolean;
  last_error_code?: string | null;
  display_message?: string | null;
}

export interface FeedLatestResponse {
  status: FeedStatusResponse;
  snapshot: FeedDailySnapshot | null;
  is_fallback: boolean;
}

export interface FeedHistoryResponse {
  items: FeedDailySnapshot[];
  next_cursor: string | null;
}

export async function getFeedStatus(): Promise<FeedStatusResponse> {
  const res = await feedAuthFetch(`${SCHEDULED_TASK_PREFIX}/feed/status`, {
    headers: getHeaders(),
  });
  return handleResponse<FeedStatusResponse>(res);
}

export async function getLatestFeedSnapshot(): Promise<FeedLatestResponse> {
  const res = await feedAuthFetch(`${SCHEDULED_TASK_PREFIX}/feed/latest`, {
    headers: getHeaders(),
  });
  return handleResponse<FeedLatestResponse>(res);
}

export async function getFeedHistory(params?: {
  before?: string | null;
  limit?: number;
}): Promise<FeedHistoryResponse> {
  const qs = new URLSearchParams();
  if (params?.before) qs.set('before', params.before);
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  const suffix = qs.toString();
  const res = await feedAuthFetch(
    `${SCHEDULED_TASK_PREFIX}/feed/history${suffix ? `?${suffix}` : ''}`,
    { headers: getHeaders() },
  );
  return handleResponse<FeedHistoryResponse>(res);
}

export async function refreshFeedSnapshot(): Promise<FeedStatusResponse> {
  const res = await feedAuthFetch(`${SCHEDULED_TASK_PREFIX}/feed/refresh`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse<FeedStatusResponse>(res);
}
